import * as React from 'react';

import { MARK_COLOR } from '@gitbook/doc-core';
import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
    sizes,
} from '@gitbook/react-uikit';
import { type EditorContainer, useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { assertNever } from '@gitbook/util-ts';

import { ContentEditorColorContext } from '../colors/ContentEditorColorContext';
import type { ColorPickerOption } from '../common/colorPickerOptions';

/**
 * Create a palette to use text/background colors.
 */
export function useColorPaletteFactory(): () => PaletteDef {
    const colorContext = React.useContext(ContentEditorColorContext);
    const editor = useEditor();
    return React.useCallback(
        () =>
            createPalette({
                search: { placeholder: 'Pick a color…' },
                groups: colorContext.groups.map((group) => ({
                    key: group.key,
                    title: group.title,
                    commands: group.colors.map((color) =>
                        createColorCommand(editor, color, group.kind)
                    ),
                })),
            }),
        [colorContext.groups, editor]
    );
}

/**
 * Create a command for a color.
 */
function createColorCommand(
    editor: EditorContainer,
    color: ColorPickerOption,
    property: 'text' | 'background'
): PaletteCommandDef {
    const activeMark = editor.value.activeMarks.find((mark) => mark.type === MARK_COLOR);
    const isActive = activeMark?.data.get(property) === color.key;

    return {
        key: `${property}${color.key}`,
        title: color.title,
        type: PaletteCommandType.RADIO,
        selected: !!isActive,
        icon: ({ size }) => {
            switch (property) {
                case 'background':
                    return <ColorIndicator size={size} backgroundColor={color.color} />;
                case 'text':
                    return <ColorIndicator size={size} color={color.color} />;
                default:
                    assertNever(property, `Unknown color property: ${property}`);
            }
        },
        trigger: () => {
            editor.change((c) => {
                c.value.activeMarks.forEach((mark) => {
                    c.removeMark(mark);
                });

                if (!isActive) {
                    c.addMark({
                        type: MARK_COLOR,
                        data: {
                            background: 'default',
                            text: 'default',
                            ...(activeMark ? activeMark.data.toObject() : {}),
                            [property]: color.key,
                        },
                    });
                }
            });
        },
    };
}

/**
 * Icon to represent a color in the palette.
 */
function ColorIndicator(props: {
    size?: number;
    color?: string;
    backgroundColor?: string;
}): React.ReactElement {
    const { size = sizes.ICON_400, color, backgroundColor } = props;

    return (
        <div
            className="flex items-center justify-center rounded"
            style={{
                width: size + 2,
                height: size + 2,
                backgroundColor,
            }}
        >
            <Icon.Type
                size={18}
                style={{
                    color,
                    fontSize: size * 0.9,
                }}
            />
        </div>
    );
}
